import type {
  AnalysisData,
  ClientData,
  ClientsData,
  CloseConversationResult,
  ConvData,
  DashboardAlert,
  DashboardConversationRow,
  DashboardData,
  LoginResult,
  MatchFeedbackResult,
  NegativeReplyResult,
  ShareLinkResult,
  UrlParams,
} from "./types";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Same-origin via nginx reverse proxy. Fase atual: /api/v1/* -> n8n /webhook/v1/*. */
const API_BASE = "/api/v1";

const AUTH_TOKEN_KEY = "wir.auth.token";

function addDevTunnelHeaders(headers: Headers): void {
  if (window.location.hostname.endsWith(".ngrok-free.app")) {
    headers.set("ngrok-skip-browser-warning", "true");
  }
}

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

// ============================================================
// URL parsing
// ============================================================

export function parseUrl(): UrlParams {
  const conv = window.location.pathname.match(/\/conv\/([0-9a-f-]+)/i);
  const client = window.location.pathname.match(/\/clientes\/([0-9a-f-]+)/i);
  const params = new URLSearchParams(window.location.search);
  return {
    id: conv ? conv[1] : params.get("id"),
    sessionId: client ? client[1] : params.get("session_id"),
    token: params.get("token"),
    mock: params.get("mock") === "1",
  };
}

export function isValidConvId(id: string | null): boolean {
  return !!id && UUID_RE.test(id);
}

// ============================================================
// Session token (localStorage) — usado quando login real existir
// ============================================================

export function getAuthToken(): string {
  return localStorage.getItem(AUTH_TOKEN_KEY) ?? "";
}

export function hasStoredAuthToken(): boolean {
  return !!localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAuthToken(t: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, t);
}

export function clearAuthToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

export async function loginAdmin(email: string, password: string): Promise<LoginResult> {
  const headers = new Headers({
    "Accept": "application/json",
    "Content-Type": "application/json",
  });
  addDevTunnelHeaders(headers);

  const res = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: JSON.stringify({ email, password }),
  });
  const body = await res.json().catch(() => ({ ok: false, error: `HTTP ${res.status}` })) as LoginResult;
  if (!res.ok || !body.ok || !body.token) {
    return {
      ok: false,
      error: body.error || `HTTP ${res.status}`,
    };
  }
  setAuthToken(body.token);
  return body;
}

// ============================================================
// Generic fetch wrapper — sempre passa Authorization
// ============================================================

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  headers.set("Accept", "application/json");
  addDevTunnelHeaders(headers);

  const res = await fetch(API_BASE + path, {
    ...init,
    headers,
    credentials: "omit",
  });

  const body = await res.json().catch(() => null);
  if (res.status === 401) {
    clearAuthToken();
    const next = encodeURIComponent(`${window.location.pathname}${window.location.search || ""}`);
    if (window.location.pathname !== "/unauthorized") {
      window.location.href = `/unauthorized?next=${next}`;
    }
    throw new ApiError(401, "Unauthorized", body);
  }
  if (!res.ok) {
    const message = body && typeof body === "object" && "error" in body
      ? String((body as { error?: unknown }).error)
      : `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

function toQueryString(params: Record<string, string | number | boolean | null | undefined>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    qs.set(key, String(value));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

function isNullishText(value: unknown): boolean {
  return value == null || String(value).trim().toLowerCase() === "null" || String(value).trim() === "";
}

function isEmptyW5ErrorAlert(alert: DashboardAlert): boolean {
  return alert.label.trim().toLowerCase() === "w5 error" && isNullishText(alert.detail);
}

function normalizeAnalysisW5Error(analysis: DashboardConversationRow["analysis"]): DashboardConversationRow["analysis"] {
  const w4 = analysis?.w4;
  if (!w4 || !("w5_error" in w4) || !isNullishText(w4.w5_error)) return analysis;
  return {
    ...analysis,
    w4: {
      ...w4,
      w5_error: null,
    },
  };
}

function normalizeConversationRow(row: DashboardConversationRow): DashboardConversationRow {
  const alerts = row.alerts?.filter((alert) => !isEmptyW5ErrorAlert(alert)) ?? [];
  return {
    ...row,
    w5_error: alerts.some((alert) => alert.label.trim().toLowerCase() === "w5 error"),
    alert_count: alerts.length,
    alerts,
    analysis: normalizeAnalysisW5Error(row.analysis),
  };
}

function normalizeDashboardData<T extends { conversations: DashboardConversationRow[] }>(data: T): T {
  return {
    ...data,
    conversations: data.conversations.map(normalizeConversationRow),
  };
}

// ============================================================
// Domain endpoints
// ============================================================

/**
 * Busca dados de uma conversa.
 * @param mock se true, busca /mock.json local (dev)
 */
export async function fetchConv(
  { id, token, mock }: UrlParams,
): Promise<ConvData> {
  if (mock) {
    // Public assets resolvem sob import.meta.env.BASE_URL (vite.config base).
    const res = await fetch(`${import.meta.env.BASE_URL}mock/conv.json`, {
      credentials: "omit",
    });
    if (!res.ok) throw new Error(`Mock HTTP ${res.status}`);
    return res.json();
  }
  if (!id) throw new Error("missing conv id");

  const params = new URLSearchParams({ id });
  if (token) params.set("token", token);
  return apiFetch<ConvData>(`/wir-conv-data?${params.toString()}`);
}

export async function fetchDashboard(
  mock: boolean,
  params: Record<string, string | number | boolean | null | undefined> = {},
): Promise<DashboardData> {
  if (mock) {
    const res = await fetch(`${import.meta.env.BASE_URL}mock/dashboard.json`, {
      credentials: "omit",
    });
    if (!res.ok) throw new Error(`Mock dashboard HTTP ${res.status}`);
    return normalizeDashboardData(await res.json() as DashboardData);
  }
  return normalizeDashboardData(await apiFetch<DashboardData>(`/wir-dashboard${toQueryString(params)}`));
}

export async function fetchClients(
  mock: boolean,
  params: Record<string, string | number | boolean | null | undefined> = {},
): Promise<ClientsData> {
  if (mock) {
    const dashboard = await fetchDashboard(true);
    const clients = new Map<string, ClientsData["clients"][number]>();
    for (const row of dashboard.conversations) {
      const current = clients.get(row.session_id) || {
        id: row.session_id,
        name: row.client_name,
        phone: row.client_phone,
        email: row.client_email,
        source: row.source,
        conversations: 0,
        opportunities: 0,
        leads: 0,
        alerts: 0,
        ego_entity_id: row.ego_entity_id,
        ego_entity_url: row.ego_entity_url,
        first_seen: row.created_at || null,
        last_seen: row.updated_at || row.created_at || null,
        last_intent: row.intent || null,
        last_classification: row.classification || null,
      };
      current.conversations += 1;
      current.opportunities += row.ego_opportunity_id ? 1 : 0;
      current.leads += row.lead_sent_at
        && (row.crm_final_emission_kind === "lead" || row.crm_final_emission_kind === "lead_realestate")
        ? 1
        : 0;
      current.alerts += row.alert_count || 0;
      clients.set(row.session_id, current);
    }
    return {
      ok: true,
      generated_at: dashboard.generated_at,
      metrics: dashboard.metrics,
      pagination: {
        page: 1,
        page_size: clients.size,
        total_items: clients.size,
        page_count: 1,
      },
      clients: Array.from(clients.values()),
    };
  }
  return apiFetch<ClientsData>(`/wir-clients${toQueryString(params)}`);
}

export async function fetchClient(
  { sessionId, mock }: UrlParams,
  params: Record<string, string | number | boolean | null | undefined> = {},
): Promise<ClientData> {
  if (mock) {
    const res = await fetch(`${import.meta.env.BASE_URL}mock/client.json`, {
      credentials: "omit",
    });
    if (!res.ok) throw new Error(`Mock client HTTP ${res.status}`);
    return normalizeDashboardData(await res.json() as ClientData);
  }
  if (!sessionId) throw new Error("missing session id");
  return normalizeDashboardData(await apiFetch<ClientData>(`/wir-client${toQueryString({ session_id: sessionId, ...params })}`));
}

export async function fetchAnalysis(
  mock: boolean,
  params: Record<string, string | number | boolean | null | undefined> = {},
): Promise<AnalysisData> {
  if (mock) return fetchDashboard(true);
  return normalizeDashboardData(await apiFetch<AnalysisData>(`/wir-analysis${toQueryString(params)}`));
}

export async function closeConversation(
  conversationId: string,
  comment?: string,
): Promise<CloseConversationResult> {
  return apiFetch<CloseConversationResult>("/wir-conversation-close", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ conversation_id: conversationId, comment }),
  });
}

export async function recordMatchFeedback(
  conversationId: string,
  matchId: string,
  feedback: "good" | "bad",
  token?: string | null,
): Promise<MatchFeedbackResult> {
  return apiFetch<MatchFeedbackResult>("/wir-match-feedback", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      conversation_id: conversationId,
      match_id: matchId,
      feedback,
      token: token || undefined,
    }),
  });
}

export async function createConversationShareLink(
  conversationId: string,
): Promise<ShareLinkResult> {
  return apiFetch<ShareLinkResult>("/wir-conversation-share-link", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ conversation_id: conversationId }),
  });
}

export async function sendConversationNegativeReply(
  conversationId: string,
  language: string,
  token?: string | null,
): Promise<NegativeReplyResult> {
  const result = await apiFetch<NegativeReplyResult | null>("/wir-conversation-negative-reply", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      conversation_id: conversationId,
      language,
      token: token || undefined,
    }),
  });
  if (!result || typeof result !== "object") {
    throw new ApiError(502, "negative_reply_empty_response", result);
  }
  return result;
}
